import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, FilterIcon } from 'lucide-react';
const CaseStudies = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [activeFilter, setActiveFilter] = useState('all');
  const filters = [{
    id: 'all',
    label: 'All Studies'
  }, {
    id: 'sports',
    label: 'Sports & Entertainment'
  }, {
    id: 'urban',
    label: 'Urban Planning'
  }, {
    id: 'arts',
    label: 'Arts & Culture'
  }, {
    id: 'retail',
    label: 'Retail & Consumer'
  }];
  const caseStudies = [{
    id: 1,
    title: 'Toronto Raptors Fan Culture',
    client: 'Professional Basketball Organization',
    category: 'sports',
    image: 'https://images.unsplash.com/photo-1504450758481-7338eba7524a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1169&q=80',
    summary: 'Analyzed the diverse fan base of the Toronto Raptors to help optimize marketing strategies and improve game-day experiences.',
    methodology: 'Mixed-method research including surveys, focus groups, and social media analysis.'
  }, {
    id: 2,
    title: 'Consumer Behavior in Urban Cores Post-COVID',
    client: 'City Planning Department',
    category: 'urban',
    image: 'https://images.unsplash.com/photo-1573164574572-cb89e39749b4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1169&q=80',
    summary: 'Examined how shopping, dining, and entertainment behaviors changed in downtown Toronto following the pandemic.',
    methodology: 'Longitudinal tracking study with mobile location data and in-depth interviews.'
  }, {
    id: 3,
    title: 'Diversity in Toronto Art Engagement',
    client: 'Major Art Museum',
    category: 'arts',
    image: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1160&q=80',
    summary: 'Investigated how different cultural communities engage with arts institutions and identified barriers to participation.',
    methodology: 'Community-based participatory research with cultural liaisons and multilingual surveys.'
  }, {
    id: 4,
    title: 'Multicultural Shopping Preferences in the GTA',
    client: 'National Retail Chain',
    category: 'retail',
    image: 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    summary: 'Analyzed shopping behaviors and preferences across different cultural communities to inform product selection and store layouts.',
    methodology: 'Ethnographic research, shopping diaries, and in-store observational studies.'
  }, {
    id: 5,
    title: 'Transit User Experience in Toronto',
    client: 'Public Transportation Authority',
    category: 'urban',
    image: 'https://images.unsplash.com/photo-1517737612767-f1e1c2d02782?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    summary: 'Evaluated user experiences across different transit modes to identify pain points and improvement opportunities.',
    methodology: 'Mobile ethnography, journey mapping, and quantitative satisfaction tracking.'
  }, {
    id: 6,
    title: 'Toronto Music Festival Audience Analysis',
    client: 'Entertainment Production Company',
    category: 'sports',
    image: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    summary: 'Profiled festival attendees to optimize programming, sponsorships, and marketing strategies.',
    methodology: 'On-site intercept surveys, social media analysis, and post-event follow-up research.'
  }];
  const filteredStudies = activeFilter === 'all' ? caseStudies : caseStudies.filter(study => study.category === activeFilter);
  return <div className="pt-20" data-id="element-290">
      {/* Hero Section */}
      <section className="bg-neutral-light py-20" data-id="element-291">
        <div className="container mx-auto px-4 md:px-6" data-id="element-292">
          <div className="max-w-3xl mx-auto text-center" data-id="element-293">
            <motion.h1 initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-4xl md:text-5xl font-display font-bold mb-6" data-id="element-294">
              Case <span className="text-toronto-blue" data-id="element-295">Studies</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="text-xl text-neutral-dark/80" data-id="element-296">
              Explore how our research has helped organizations make data-driven
              decisions.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Search and Filter */}
      <section className="py-8 bg-white border-b" data-id="element-297">
        <div className="container mx-auto px-4 md:px-6" data-id="element-298">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-299">
            <div className="relative max-w-md w-full" data-id="element-300">
              <input type="text" placeholder="Search case studies..." className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-toronto-blue/30" data-id="element-301" />
              <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-dark/50" data-id="element-302" />
            </div>
            <div className="flex items-center overflow-x-auto pb-2 md:pb-0 gap-2" data-id="element-303">
              <FilterIcon size={18} className="text-neutral-dark/70 mr-1" data-id="element-304" />
              <span className="text-sm font-medium mr-2" data-id="element-305">Filter:</span>
              {filters.map(filter => <button key={filter.id} onClick={() => setActiveFilter(filter.id)} className={`px-3 py-1 text-sm rounded-full whitespace-nowrap ${activeFilter === filter.id ? 'bg-toronto-blue text-white' : 'bg-neutral-light text-neutral-dark hover:bg-neutral-light/70'}`} data-id="element-306">
                  {filter.label}
                </button>)}
            </div>
          </div>
        </div>
      </section>
      {/* Case Studies Grid */}
      <section className="py-16 bg-white" data-id="element-307">
        <div className="container mx-auto px-4 md:px-6" data-id="element-308">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-309">
            {filteredStudies.map((study, index) => <motion.div key={study.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="bg-white border rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow" data-id="element-310">
                <div className="h-48 overflow-hidden" data-id="element-311">
                  <img src={study.image} alt={study.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" data-id="element-312" />
                </div>
                <div className="p-6" data-id="element-313">
                  <div className="flex items-center mb-3" data-id="element-314">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${study.category === 'sports' ? 'bg-accent-coral/20 text-accent-coral' : study.category === 'urban' ? 'bg-accent-teal/20 text-accent-teal' : study.category === 'arts' ? 'bg-accent-ochre/20 text-accent-ochre' : 'bg-accent-slate/20 text-accent-slate'}`} data-id="element-315">
                      {filters.find(f => f.id === study.category)?.label}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2" data-id="element-316">{study.title}</h3>
                  <p className="text-sm text-neutral-dark/70 mb-3" data-id="element-317">
                    Client: {study.client}
                  </p>
                  <p className="text-neutral-dark/80 mb-4" data-id="element-318">{study.summary}</p>
                  <div className="flex items-center justify-between" data-id="element-319">
                    <div data-id="element-320">
                      <span className="text-sm font-medium text-neutral-dark/70" data-id="element-321">
                        Methodology:
                      </span>
                      <p className="text-sm" data-id="element-322">{study.methodology}</p>
                    </div>
                    <button className="text-toronto-blue hover:underline text-sm font-medium" data-id="element-323">
                      Read More
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>
          {filteredStudies.length === 0 && <div className="text-center py-12" data-id="element-324">
              <p className="text-lg text-neutral-dark/70" data-id="element-325">
                No case studies found matching your filter.
              </p>
              <button onClick={() => setActiveFilter('all')} className="mt-4 text-toronto-blue hover:underline" data-id="element-326">
                Clear filters
              </button>
            </div>}
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-neutral-light" data-id="element-327">
        <div className="container mx-auto px-4 md:px-6" data-id="element-328">
          <div className="max-w-3xl mx-auto text-center" data-id="element-329">
            <h2 className="text-3xl font-display font-bold mb-6" data-id="element-330">
              Need Similar Research for Your Organization?
            </h2>
            <p className="text-lg text-neutral-dark/80 mb-8" data-id="element-331">
              Our team can design a custom research project to address your
              specific challenges and opportunities.
            </p>
            <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="bg-toronto-blue text-white px-6 py-3 rounded font-medium hover:bg-toronto-blue/90 transition-colors" data-id="element-332">
              Contact Us to Discuss Your Project
            </motion.button>
          </div>
        </div>
      </section>
    </div>;
};
export default CaseStudies;